import type { FormInstance } from 'antd/es/form';

import type { VenueEditFormValues } from '../../../../types';

type TimeSlot = {
  startTime: number;
  endTime: number;
  index: number;
};

export const checkTimeOverlap = (
  times: VenueEditFormValues['operatingHours'][0]['times']
): {
  hasOverlap: boolean;
  overlappingPairs: Array<{ prevIndex: number; nextIndex: number }>;
} => {
  if (times.length <= 1) {
    return { hasOverlap: false, overlappingPairs: [] };
  }

  const validTimes: TimeSlot[] = times
    .map((time, index) => ({ ...time, index }))
    .filter(
      (time): time is TimeSlot =>
        time.startTime !== null &&
        time.endTime !== null &&
        time.startTime < time.endTime
    );

  if (validTimes.length <= 1) {
    return { hasOverlap: false, overlappingPairs: [] };
  }

  validTimes.sort((a, b) => a.startTime - b.startTime);

  const overlappingPairs: Array<{ prevIndex: number; nextIndex: number }> = [];

  for (let i = 0; i < validTimes.length - 1; i++) {
    const current = validTimes[i];
    const next = validTimes[i + 1];

    if (current.endTime > next.startTime) {
      overlappingPairs.push({
        prevIndex: current.index,
        nextIndex: next.index,
      });
    }
  }

  return {
    hasOverlap: overlappingPairs.length > 0,
    overlappingPairs,
  };
};

export const validateTabSwitch = async (
  form: FormInstance<VenueEditFormValues>,
  prevDayIndex: number
): Promise<boolean> => {
  const openingStatus: VenueEditFormValues['operatingHours'][0]['status'] =
    form.getFieldValue(['operatingHours', prevDayIndex, 'status']);

  if (openingStatus === 'allDay' || openingStatus === 'closed') {
    return true;
  }

  const times: VenueEditFormValues['operatingHours'][0]['times'] =
    form.getFieldValue(['operatingHours', prevDayIndex, 'times']) || [];

  const fieldNames = times.flatMap((_timeRange, timeRangeIndex) => [
    ['operatingHours', prevDayIndex, 'times', timeRangeIndex, 'startTime'],
    ['operatingHours', prevDayIndex, 'times', timeRangeIndex, 'endTime'],
  ]);

  try {
    await form.validateFields(fieldNames);
    await form.validateFields([['operatingHours', prevDayIndex, 'times']]);
    return true;
  } catch {
    return false;
  }
};
